import React, { useEffect, useState } from "react";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import {
  TextStyle, Color, FontFamily, FontSize, LineHeight, BackgroundColor,
} from "@tiptap/extension-text-style";
import { TableRow } from "@tiptap/extension-table";
import { DocTable, DocTableCell, DocTableHeader, PageBreak } from "./extensions";
import { MergeField } from "../contrato/MergeField";
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Undo2, Redo2, Save, Eye, RotateCcw, Tag, Loader2,
  Table as TableIcon, Rows3, Columns3, Trash2, Baseline, PaintBucket, PenLine, Plus, X, SeparatorHorizontal,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { useToast } from "../../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FONTES = [
  { label: "Padrão", value: "" },
  { label: "Arial / Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "Times", value: "'Times New Roman', Times, serif" },
  { label: "Courier", value: "'Courier New', Courier, monospace" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Verdana", value: "Verdana, sans-serif" },
];
const TAMANHOS = ["10px", "11px", "12px", "13px", "14px", "16px", "18px", "20px", "24px"];
const ESPACAMENTOS = [
  { label: "Simples", value: "1.2" },
  { label: "1,5", value: "1.5" },
  { label: "Duplo", value: "2.0" },
];
const BORDAS = [
  { label: "Com bordas (preta)", value: "bordered" },
  { label: "Bordas coloridas", value: "colored" },
  { label: "Sem bordas (transparente)", value: "none" },
];

const TbBtn = ({ onClick, active, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
      active ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
    }`}
  >
    {children}
  </button>
);
const Sep = () => <div className="w-px h-5 bg-slate-200 mx-1" />;
const SelBox = ({ value, onChange, options, title, testid, width = 120 }) => (
  <select
    title={title}
    data-testid={testid}
    value={value}
    onMouseDown={(e) => e.stopPropagation()}
    onChange={(e) => onChange(e.target.value)}
    className="h-8 text-xs border border-slate-200 rounded-md px-1.5 text-slate-600 bg-white"
    style={{ width }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const DocumentoEditor = ({ docKey }) => {
  const { toast } = useToast();
  const [campos, setCampos] = useState([]);
  const [padrao, setPadrao] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigs, setSigs] = useState([{ nome: "", cargo: "" }]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle, Color, FontFamily, FontSize, LineHeight, BackgroundColor,
      DocTable.configure({ resizable: false }),
      TableRow, DocTableHeader, DocTableCell,
      PageBreak,
      MergeField,
    ],
    content: "",
    editorProps: { attributes: { class: "doc-prose", "data-testid": "documento-editor-area" } },
  });

  useEffect(() => {
    if (!editor) return;
    let ativo = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/documentos/modelo/${docKey}`);
        if (!ativo) return;
        setCampos(data.campos || []);
        setPadrao(data.padrao || "");
        editor.commands.setContent(data.html || data.padrao || "");
      } catch (e) {
        toast({ title: "Erro ao carregar o modelo", description: e?.response?.data?.detail || "" });
      } finally {
        if (ativo) setLoading(false);
      }
    })();
    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, docKey]);

  const inserir = (field, chip) =>
    editor?.chain().focus().insertContent({ type: "mergeField", attrs: { field, label: chip } }).run();

  const escHtml = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inserirAssinaturas = () => {
    if (!editor) return;
    const lista = sigs.filter((s) => (s.nome || s.cargo).trim());
    const usar = lista.length ? lista : [{ nome: "", cargo: "" }];
    const cells = usar.map((s) =>
      `<td style="text-align:center">______________________________________<br/>` +
      `<b>${escHtml(s.nome)}</b>${s.cargo ? `<br/>${escHtml(s.cargo)}` : ""}</td>`
    ).join("");
    const html = `<p></p><table class="doc-table none" style="width:100%"><tbody><tr>${cells}</tr></tbody></table><p></p>`;
    editor.chain().focus().insertContent(html).run();
    setSigOpen(false);
  };

  const salvar = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      await axios.put(`${API}/documentos/modelo/${docKey}`, { html: editor.getHTML() });
      toast({ title: "Modelo salvo", description: "Será usado ao imprimir o documento de cada residente." });
    } catch (e) {
      toast({ title: "Erro ao salvar", description: e?.response?.data?.detail || "Tente novamente." });
    } finally { setSaving(false); }
  };

  const preVisualizar = async () => {
    if (!editor) return;
    setPreviewing(true);
    try {
      const { data } = await axios.post(
        `${API}/documentos/modelo/${docKey}/preview`,
        { html: editor.getHTML() },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({ title: "Erro na pré-visualização", description: e?.response?.data?.detail || "" });
    } finally { setPreviewing(false); }
  };

  const restaurar = () => {
    if (!editor || !padrao) return;
    editor.commands.setContent(padrao);
    toast({ title: "Modelo padrão restaurado", description: "Clique em Salvar para confirmar." });
  };

  if (loading || !editor) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center" data-testid="documento-editor-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando modelo…
      </div>
    );
  }

  const inTable = editor.isActive("table");

  return (
    <div className="space-y-4" data-testid={`documento-editor-${docKey}`}>
      <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 flex items-start gap-2">
        <Tag className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-600 leading-relaxed">
          Os campos em <span className="mf" style={{ fontSize: 10 }}>azul</span> são preenchidos automaticamente com os
          dados de cada residente. Use os botões <b>“Inserir campo”</b> para adicioná-los. Editor completo: fonte, cor,
          espaçamento e tabelas (com bordas coloridas ou transparentes).
        </p>
      </div>

      {/* Paleta de campos */}
      <div className="flex flex-wrap gap-2" data-testid="documento-campos-paleta">
        {campos.map((g) => (
          <DropdownMenu key={g.grupo}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs" data-testid={`doc-inserir-grupo-${g.grupo}`}>
                <Tag className="w-3.5 h-3.5 text-blue-600" /> {g.grupo}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              <DropdownMenuLabel className="text-xs">{g.grupo}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {g.itens.map((it) => (
                <DropdownMenuItem key={it.key} onClick={() => inserir(it.key, it.chip)} data-testid={`doc-inserir-campo-${it.key}`} className="text-sm cursor-pointer">
                  {it.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
      </div>

      {/* Editor */}
      <div className="doc-editor rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-slate-100 bg-slate-50/80">
          <TbBtn testid="doc-bold" title="Negrito" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-italic" title="Itálico" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-underline" title="Sublinhado" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="w-4 h-4" /></TbBtn>
          <Sep />
          <SelBox testid="doc-fonte" title="Tipo de letra" width={130} value={editor.getAttributes("textStyle").fontFamily || ""}
            onChange={(v) => v ? editor.chain().focus().setFontFamily(v).run() : editor.chain().focus().unsetFontFamily().run()}
            options={FONTES} />
          <SelBox testid="doc-tamanho" title="Tamanho" width={72} value={editor.getAttributes("textStyle").fontSize || ""}
            onChange={(v) => v ? editor.chain().focus().setFontSize(v).run() : editor.chain().focus().unsetFontSize().run()}
            options={[{ label: "Tam.", value: "" }, ...TAMANHOS.map((t) => ({ label: t.replace("px", ""), value: t }))]} />
          <SelBox testid="doc-espacamento" title="Espaçamento entre linhas" width={92} value={editor.getAttributes("paragraph").lineHeight || ""}
            onChange={(v) => v ? editor.chain().focus().setLineHeight(v).run() : editor.chain().focus().unsetLineHeight().run()}
            options={[{ label: "Espaç.", value: "" }, ...ESPACAMENTOS]} />
          <Sep />
          <label className="w-8 h-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 cursor-pointer relative" title="Cor do texto" data-testid="doc-cor-texto">
            <Baseline className="w-4 h-4" />
            <input type="color" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} />
          </label>
          <label className="w-8 h-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 cursor-pointer relative" title="Cor de realce" data-testid="doc-cor-fundo">
            <PaintBucket className="w-4 h-4" />
            <input type="color" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => editor.chain().focus().setBackgroundColor(e.target.value).run()} />
          </label>
          <TbBtn testid="doc-limpar-cor" title="Remover cores" onClick={() => editor.chain().focus().unsetColor().unsetBackgroundColor().run()}><span className="text-[10px] font-bold">A̶</span></TbBtn>
          <Sep />
          <TbBtn testid="doc-h1" title="Título 1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-h2" title="Título 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-h3" title="Título 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-ul" title="Lista" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-ol" title="Lista numerada" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></TbBtn>
          <Sep />
          <TbBtn testid="doc-align-left" title="Esquerda" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-align-center" title="Centralizar" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-align-right" title="Direita" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-align-justify" title="Justificar" active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}><AlignJustify className="w-4 h-4" /></TbBtn>
          <Sep />
          {/* Tabela */}
          <TbBtn testid="doc-tabela-inserir" title="Inserir tabela" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run()}><TableIcon className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-linha-add" title="Adicionar linha" onClick={() => editor.chain().focus().addRowAfter().run()}><Rows3 className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-coluna-add" title="Adicionar coluna" onClick={() => editor.chain().focus().addColumnAfter().run()}><Columns3 className="w-4 h-4" /></TbBtn>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" title="Mais opções de tabela" data-testid="doc-tabela-menu" onMouseDown={(e) => e.preventDefault()} className="h-8 px-2 flex items-center rounded-md text-slate-600 hover:bg-slate-100 text-xs">Tabela ▾</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="text-sm">
              <DropdownMenuLabel className="text-xs">Bordas</DropdownMenuLabel>
              {BORDAS.map((b) => (
                <DropdownMenuItem key={b.value} disabled={!inTable} onClick={() => editor.chain().focus().updateAttributes("table", { borderStyle: b.value }).run()} data-testid={`doc-borda-${b.value}`} className="cursor-pointer">{b.label}</DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={!inTable} onClick={() => editor.chain().focus().toggleHeaderRow().run()} className="cursor-pointer" data-testid="doc-tabela-header">Alternar cabeçalho</DropdownMenuItem>
              <DropdownMenuItem disabled={!inTable} onClick={() => editor.chain().focus().deleteRow().run()} className="cursor-pointer" data-testid="doc-linha-del">Excluir linha</DropdownMenuItem>
              <DropdownMenuItem disabled={!inTable} onClick={() => editor.chain().focus().deleteColumn().run()} className="cursor-pointer" data-testid="doc-coluna-del">Excluir coluna</DropdownMenuItem>
              <DropdownMenuItem disabled={!inTable} onClick={() => editor.chain().focus().deleteTable().run()} className="cursor-pointer text-red-600" data-testid="doc-tabela-del"><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Excluir tabela</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Sep />
          <TbBtn testid="doc-assinatura" title="Inserir assinatura" onClick={() => setSigOpen(true)}><PenLine className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-quebra-pagina" title="Inserir quebra de página" onClick={() => editor.chain().focus().setPageBreak().run()}><SeparatorHorizontal className="w-4 h-4" /></TbBtn>
          <Sep />
          <TbBtn testid="doc-undo" title="Desfazer" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-4 h-4" /></TbBtn>
          <TbBtn testid="doc-redo" title="Refazer" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-4 h-4" /></TbBtn>
        </div>
        <EditorContent editor={editor} />
      </div>

      {/* Ações */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="outline" className="gap-2 text-slate-600" onClick={restaurar} data-testid="documento-restaurar-btn">
          <RotateCcw className="w-4 h-4" /> Restaurar modelo padrão
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={preVisualizar} disabled={previewing} data-testid="documento-preview-btn">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Pré-visualizar
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar} disabled={saving} data-testid="documento-salvar-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar modelo
          </Button>
        </div>
      </div>

      <Dialog open={sigOpen} onOpenChange={setSigOpen}>
        <DialogContent aria-describedby={undefined} data-testid="assinatura-modal">
          <DialogHeader>
            <DialogTitle>Inserir campos de assinatura</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Adicione um ou mais signatários. Cada um vira uma linha de assinatura com nome e cargo, inserida lado a lado no final do documento (você pode reposicionar depois).
            </p>
            {sigs.map((s, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`assinatura-linha-${i}`}>
                <Input placeholder="Nome" value={s.nome} data-testid={`assinatura-nome-${i}`}
                  onChange={(e) => setSigs((a) => a.map((x, j) => j === i ? { ...x, nome: e.target.value } : x))} />
                <Input placeholder="Cargo (opcional)" value={s.cargo} data-testid={`assinatura-cargo-${i}`}
                  onChange={(e) => setSigs((a) => a.map((x, j) => j === i ? { ...x, cargo: e.target.value } : x))} />
                {sigs.length > 1 && (
                  <button type="button" title="Remover" data-testid={`assinatura-remover-${i}`}
                    className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50"
                    onClick={() => setSigs((a) => a.filter((_, j) => j !== i))}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="assinatura-add"
              onClick={() => setSigs((a) => [...a, { nome: "", cargo: "" }])}>
              <Plus className="w-3.5 h-3.5" /> Adicionar signatário
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={inserirAssinaturas} data-testid="assinatura-inserir-btn">
              Inserir assinatura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentoEditor;
